﻿import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import 'react-loading-skeleton/dist/skeleton.css';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import '@/styles/ITReportStyles.scss';
import UserAvatarSVG from '@/components/UserAvatarSVG'
import MallOutagesChart from './MallOutagesChart';
import ExchangeEmailStatsChart from './ExchangeEmailStatsChart';
import DeviceInventorySummary from './DeviceInventorySummary';
/*import PrinterUsageChart from './PrinterUsageChart';*/
import ITActionItemsSummary from './ITActionItemsSummary';
import SharepointSiteUsage from './SharepointSiteUsage';
import RubrikBackupSummary from './RubrikBackupSummary';
import ProxmoxClusterSummary from './ProxmoxClusterSummary';
import GithubActivitySummary from './GithubActivitySummary';
import ExecutiveSummary from '@/components/Insights/ExecutiveSummary';
import GroupSummary from '@/components/Insights/GroupSummary';
import DataCoverageStrip from '@/components/Insights/DataCoverageStrip';
import TenantStatementsChart from './TenantStatementsChart';
/*import UniversalPrintStatsChart from './UniversalPrintStatsChart';*/
import PortaCallReporting from './PortaCallReporting';
import RouterFleetSummary from './RouterFleetSummary';
import RiskRegister from './RiskRegister';
import OverallHealthScore from './OverallHealthScore';
import ComprehensiveSecurityDashboard from './ComprehensiveSecurityDashboard'; // Unified security dashboard
import GithubSecuritySummary from './GithubSecuritySummary'; // GitHub security (GHAS, Dependabot)
import GithubProjectFlowSummary from './GithubProjectFlowSummary'; // GitHub Project v2 board flow
import GithubIssuesHealthSummary from './GithubIssuesHealthSummary'; // GitHub issues health
import SentryProductionSummary from './SentryProductionSummary'; // Sentry production health
import MsPurviewSummary from './MsPurviewSummary'; // Microsoft Purview Compliance
import AutomationSelfHealingSummary from './AutomationSelfHealingSummary'; // Automation & self-healing (monitoring + auto-remediation)
import Microsoft365Compliance from './Microsoft365Compliance'; // Microsoft 365 config & compliance gaps
import { PrintModeContext } from './PrintModeContext';

const ITReport = () => {
  const { user } = useAuth();
  const reportRef = useRef();
  const [isPrintMode, setIsPrintMode] = useState(false);

  // Handle print events - activate print mode before printing for ReCharts to resize properly
  useEffect(() => {
    const handleBeforePrint = () => {
      setIsPrintMode(true);
      document.body.classList.add('print-exporting');
    };

    const handleAfterPrint = () => {
      setIsPrintMode(false);
      document.body.classList.remove('print-exporting');
    };

    // Listen for browser print events
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  // Derive last month label explicitly for Rubrik section & header
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthLabel = lastMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  // Period coverage shown in the executive header so directors can see at a
  // glance which calendar window the metrics describe.
  const periodStart = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1);
  const periodEnd = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0);
  const periodRange = `${periodStart.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })} – ${periodEnd.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}`;
  const generatedAt = now.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const preparedByName = `${user?.FirstName ?? user?.firstName ?? ''} ${user?.LastName ?? user?.lastName ?? ''}`.trim();

  return (
    <PrintModeContext.Provider value={isPrintMode}>
      <div className="itreport" ref={reportRef}>
        <div className="main-content itreport-content">
          <div className="pdf-exclude itreport-greeting">
            <UserAvatarSVG />
            <span>Hello {(user?.FirstName ?? user?.firstName) || ''}</span>
          </div>
          <h1 className="itreport-title">{`IT Report: ${lastMonthLabel}`}</h1>

          {/* Executive header — shown both on screen and on the printed report so
              directors immediately see the reporting window, who prepared the
              report, and when it was generated. */}
          <div className="itreport-meta">
            <div className="itreport-meta-item">
              <span className="itreport-meta-label">Reporting period</span>
              <span className="itreport-meta-value">{periodRange}</span>
            </div>
            <div className="itreport-meta-item">
              <span className="itreport-meta-label">Prepared by</span>
              <span className="itreport-meta-value">{preparedByName || 'IT Department'}</span>
            </div>
            <div className="itreport-meta-item">
              <span className="itreport-meta-label">Generated</span>
              <span className="itreport-meta-value">{generatedAt}</span>
            </div>
            <div className="itreport-meta-item">
              <span className="itreport-meta-label">Audience</span>
              <span className="itreport-meta-value">Executive / Director circulation</span>
            </div>
          </div>
          <p className="itreport-disclaimer pdf-exclude">
            Scores marked with an asterisk (*) indicate provisional metrics where data
            confidence is below 100%. See the Overall Health Score footer for the full
            scoring methodology.
          </p>

          {/* Handover orientation — this report doubles as a continuity/handover artifact.
              It gives the incoming IT lead and the director a single, self-explanatory
              picture of the department: where it stands, where the risks are, and what
              needs attention. Every section is computed from live data and carries its
              own plain-language explanation, so it remains readable without AI narrative. */}
          <div className="itreport-handover">
            <span className="itreport-handover-label">How to read this report</span>
            <p>
              This is the single source of truth for the IT department&apos;s health, services,
              infrastructure and risks for the reporting period above. The <strong>Overall IT
              Service Health</strong> score at the top is the headline KPI; each section below
              breaks down a specific area, opens with a plain-language &quot;what this means&quot;
              summary, and flags anything that needs attention. Sections turn red/amber when a
              metric is outside its healthy range. The <strong>Risk Register</strong> at the end
              captures known risks and owners for continuity.
            </p>
          </div>

          {/* Deterministic data-freshness/coverage strip. Always rendered (independent of
              AI) so the reader can immediately see which telemetry feeds are reporting,
              missing, or stale — directly answering "what is NOT in this report?". */}
          <DataCoverageStrip />

          {/* Overall Health Score */}
          <section className="itreport-section">
            <OverallHealthScore />
          </section>

          {/*Executive Summary*/}
          <section className="itreport-section">
            <ExecutiveSummary />
          </section>

          {/* Comprehensive Security Dashboard */}
          <section className="itreport-section">
            <ComprehensiveSecurityDashboard />
            <GroupSummary group="security" title="Enterprise Security Analysis" />
          </section>

          {/* GitHub Security - GHAS, CodeQL, Dependabot */}
          <section className="itreport-section">
            <GithubSecuritySummary owner="Capital-Land-Management-Services" repo="CapTrac-ReactCore" />
            <GroupSummary group="githubsecurity" title="Repository Security Analysis" />
          </section>

          {/* GitHub Project Board Flow - Project v2 workflow */}
          <section className="itreport-section">
            <GithubProjectFlowSummary orgName="Capital-Land-Management-Services" projectNumber={28} />
            <GroupSummary group="githubproject" title="Project Board Workflow" />
          </section>

          {/* GitHub Issues Health - Repository issues tracking */}
          <section className="itreport-section">
            <GithubIssuesHealthSummary owner="Capital-Land-Management-Services" repo="CapTrac-ReactCore" />
            <GroupSummary group="githubissues" title="Issues Health" />
          </section>

          {/* Sentry Production Health - Error tracking */}
          <section className="itreport-section">
            <SentryProductionSummary />
            <GroupSummary group="sentry" title="Production Error Tracking" />
          </section>

          {/* Microsoft 365 Configuration & Compliance Gaps */}
          <section className="itreport-section">
            <Microsoft365Compliance />
            <GroupSummary group="m365compliance" title="Microsoft 365 Configuration" />
          </section>

          {/* Microsoft Purview Compliance */}
          <section className="itreport-section">
            <MsPurviewSummary />
            <GroupSummary group="purview" title="Compliance & Data Governance" />
          </section>

          {/* IT Related Action Items (support tickets) */}
          <section className="itreport-section">
            <ITActionItemsSummary />
            <GroupSummary group="actionitems" title="IT Related Action Items" />
          </section>

          {/* Automation & Self-Healing — monitoring coverage + auto-remediation impact */}
          <section className="itreport-section">
            <AutomationSelfHealingSummary />
            <GroupSummary group="automation" title="Automation & Self-Healing" />
          </section>

          {/* Backup & Recovery */}
          <section className="itreport-section">
            <RubrikBackupSummary />
            <GroupSummary group="rubrik" title="Rubrik" />
          </section>

          {/* Virtualization & Infrastructure */}
          <section className="itreport-section">
            <ProxmoxClusterSummary />
            <GroupSummary group="proxmox" title="Proxmox" />
          </section>

          {/* Development & Repos */}
          <section className="itreport-section">
            <GithubActivitySummary owner="Capital-Land-Management-Services" repo="CapTrac-ReactCore" />
            <GroupSummary group="github" title="GitHub" />
          </section>

          {/* Device & Collaboration */}
          <section className="itreport-section">
            <DeviceInventorySummary />
            <GroupSummary group="intune" title="Devices" />
          </section>

          {/* SharePoint Sites and Usage */}
          <section className="itreport-section">
            <SharepointSiteUsage />
            <GroupSummary group="sharepoint" title="SharePoint" />
          </section>

          {/* Network Reliability & Outages */}
          <section className="itreport-section">
            <MallOutagesChart />
            <GroupSummary group="outages" title="Outages" />
          </section>

          {/* Router Fleet Management */}
          <section className="itreport-section">
            <RouterFleetSummary />
            <GroupSummary group="routerfleet" title="Router Fleet" />
          </section>

          {/* Telephony & Call Reporting */}
          <section className="itreport-section">
            <PortaCallReporting />
            <GroupSummary group="telephony" title="Calls" />
          </section>

          {/* MDA Tenant Statements */}
          <section className="itreport-section">
            <TenantStatementsChart />
            <GroupSummary group="mda" title="Tenant Statements" />
          </section>

          {/* Exchange Online */}
          <section className="itreport-section">
            <ExchangeEmailStatsChart />
            <GroupSummary group="exchange" title="Email" />
          </section>

          {/* Risk Register */}
          <section className="itreport-section risk-register-section">
            <RiskRegister />
          </section>

          <ReactTooltip id="it-report-tooltips" />
        </div>
      </div>
    </PrintModeContext.Provider>
  );
};

export default ITReport;